import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Link,
} from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";

interface NewsItem {
  title: string;
  link: string;
  pubDate?: string;
  description?: string;
  imageURL?: string | null;
  textContent?: string | null;
}

interface NewsFeed {
  items: NewsItem[];
  title?: string;
}

// Function to extract image URL and clean text content from HTML
function differnetContent(text: string) {
  if (!text) return [null, ""];

  // More comprehensive regex pattern to match image URLs in various HTML formats
  // This handles both self-closing tags and regular tags with or without attributes
  const imageURLPattern = /<img[^>]+src=['"]([^'"]+)['"][^>]*>/i;
  const htmlTagPattern = /<\/?[^>]+(>|$)/g;

  const imageURLMatch = text.match(imageURLPattern);

  console.log("HTML content to parse:", text.substring(0, 100) + "...");

  let imageURL = null;
  let otherTextContent = null;

  if (imageURLMatch) {
    imageURL = imageURLMatch[1];
    console.log("Found image match:", imageURLMatch[0]);
    console.log("Extracted URL:", imageURL);

    // Remove the image tag from the content
    otherTextContent = text
      .replace(imageURLMatch[0], "")
      .replace(/<br\s*\/?>/gi, " ") // Replace <br> with space instead of removing
      .replace(htmlTagPattern, "");
  } else {
    console.log("No image match found in content");
    otherTextContent = text
      .replace(/<br\s*\/?>/gi, " ")
      .replace(htmlTagPattern, "");
  }

  // Trim whitespace and normalize spaces
  otherTextContent = otherTextContent.trim().replace(/\s+/g, " ");

  return [imageURL, otherTextContent];
}

const LeagueNews = ({ channel }: { channel: string }) => {
  const [newsFeed, setNewsFeed] = useState<NewsFeed | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousChannelRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  const fetchNewsFeed = useCallback(async (channelName: string) => {
    if (!channelName) return;

    setLoading(true);
    setError(null);

    try {
      // Option 1: Use a CORS proxy (for development only)
      const corsProxy = "https://api.allorigins.win/raw?url=";
      const NEWS_FEED_URL = `${corsProxy}${encodeURIComponent(
        `http://feeds.nahl.com/${channelName}.xml`
      )}`;

      const response = await fetch(NEWS_FEED_URL);
      const xmlText = await response.text();

      // Only update state if the component is still mounted
      if (!isMountedRef.current) return;

      // Simple XML parsing (basic approach - you might want to use a proper XML parser)
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "text/xml");

      // Extract items
      const items: NewsItem[] = [];
      const itemElements = xmlDoc.querySelectorAll("item");
      console.log(itemElements);

      itemElements.forEach((item) => {
        const title = item.querySelector("title")?.textContent || "";
        const link = item.querySelector("link")?.textContent || "";
        const pubDate = item.querySelector("pubDate")?.textContent;
        const description =
          item.querySelector("description")?.textContent || "";

        // Process description to extract image URL and clean text
        const [imageURL, textContent] = differnetContent(description);

        // Debug: Log the extracted image URL
        if (imageURL) {
          console.log(`Found image URL in item ${title}: ${imageURL}`);
        }

        items.push({
          title,
          link,
          pubDate,
          description,
          imageURL,
          textContent,
        });
      });

      const feedTitle = xmlDoc.querySelector("channel > title")?.textContent;

      setNewsFeed({
        items: items.slice(0, 5), // Limit to 5 items
        title: feedTitle,
      });
    } catch (err) {
      if (isMountedRef.current) {
        console.error("Error fetching news feed:", err);
        setError("Failed to load news feed");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Reset isMountedRef on mount
  useEffect(() => {
    isMountedRef.current = true;

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle fetching news feed when channel changes
  useEffect(() => {
    // Skip if channel is empty
    if (!channel) return;

    // Skip if the channel hasn't changed
    if (previousChannelRef.current === channel) return;

    // Update the ref to the current channel
    previousChannelRef.current = channel;

    // Fetch the news feed
    fetchNewsFeed(channel);
  }, [channel, fetchNewsFeed]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h6" sx={{ py: 1 }}>
        League News
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && <Typography color="error">{error}</Typography>}
      <Box sx={{ overflowY: "auto", height: "700px" }}>
        {newsFeed?.items &&
          newsFeed.items.map((item, index) => (
            <Card key={index} sx={{ backgroundColor: "#f5f5f5" }}>
              <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                {/* Display image if available */}
                {item.imageURL && (
                  <Box sx={{ mb: 1, display: "flex" }}>
                    <Box
                      component="img"
                      src={item.imageURL}
                      alt=""
                      sx={{
                        maxWidth: "100%",
                        width: 1,
                        objectFit: "contain",
                        borderRadius: 1,
                        cursor: "pointer",
                        "&:hover": {
                          transform: "scale(1.02)",
                          transition: "transform 0.2s",
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(item.link, "_blank");
                      }}
                    />
                  </Box>
                )}

                <Link
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "inherit",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {item.title}
                  </Typography>
                </Link>

                {/* Display cleaned text content */}
                {item.textContent && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: "text.secondary" }}
                  >
                    {item.textContent.length > 120
                      ? `${item.textContent.substring(0, 120)}...`
                      : item.textContent}
                  </Typography>
                )}

                {item.pubDate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {new Date(item.pubDate).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
      </Box>
    </Box>
  );
};

export default LeagueNews;
